import { css } from "@emotion/react";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, Clock3 } from "lucide-react";
import type React from "react";
import { useEffect, useEffectEvent, useMemo, useState } from "react";
import type {
	InventoryAvailabilityCalendarDateDto,
	InventoryAvailabilityCalendarEventDto,
	InventoryAvailabilityCalendarResponseDto,
} from "@/lib/storefront-api";
import { storefrontSdk } from "@/storefrontsdk";
import { useCheckoutFormState } from "../../hooks/useCheckoutFormState";
import { ensureDateTimeSegment } from "../../hooks/useInventoryState";
import { useProductDetailsState } from "../../hooks/useProductDetailsState";
import { useSiteLayoutState } from "../../hooks/useSiteLayoutState";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { useTailwindMediaQueries } from "../../hooks/useTailwindMediaQueries";
import { useThemeState } from "../../hooks/useThemeState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import { ReservationDates } from "../CheckoutForm/ReservationDates";

type CalendarView = "month" | "week" | "day";
const calendarViews: CalendarView[] = ["month", "week", "day"];
const hoursInDay = Array.from({ length: 24 }, (_, hour) => hour);
const monthGridLastIndex = 41;

const normalizeCalendarView = (value?: string | null): CalendarView =>
	calendarViews.includes(value as CalendarView)
		? (value as CalendarView)
		: "month";

const parseTimeToMinutes = (timeValue?: string | null) => {
	if (!timeValue) {
		return undefined;
	}
	const [hoursRaw, minutesRaw] = timeValue.split(":");
	const hours = Number.parseInt(hoursRaw ?? "", 10);
	const minutes = Number.parseInt(minutesRaw ?? "", 10);
	if (
		Number.isNaN(hours) ||
		Number.isNaN(minutes) ||
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59
	) {
		return undefined;
	}
	return hours * 60 + minutes;
};

type CalendarEvent = InventoryAvailabilityCalendarEventDto & {
	startAt?: dayjs.Dayjs;
	endAt?: dayjs.Dayjs;
};

type CalendarDate = InventoryAvailabilityCalendarDateDto & {
	dayKey: string;
};

type TimelineEventLayout = {
	event: CalendarEvent;
	top: number;
	height: number;
	laneIndex: number;
	laneCount: number;
};

const eventTextToLines = (text?: string | null): string[] => {
	if (!text) {
		return [];
	}
	return text
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter((line) => line.length > 0);
};

const normalizeEvent = (
	event: InventoryAvailabilityCalendarEventDto,
): CalendarEvent => {
	const startAt = event.start ? dayjs(event.start) : undefined;
	const rawEndAt = event.end ? dayjs(event.end) : undefined;
	const endAt = rawEndAt?.isValid()
		? rawEndAt.isAfter(startAt) || !startAt
			? rawEndAt
			: startAt
		: startAt;

	return {
		...event,
		startAt: startAt?.isValid() ? startAt : undefined,
		endAt: endAt?.isValid() ? endAt : undefined,
	};
};

const normalizeDate = (
	dateValue: InventoryAvailabilityCalendarDateDto,
): CalendarDate | undefined => {
	if (!dateValue.Day) {
		return undefined;
	}
	const parsedDay = dayjs(dateValue.Day);
	if (!parsedDay.isValid()) {
		return undefined;
	}
	return {
		...dateValue,
		dayKey: parsedDay.format("YYYY-MM-DD"),
	};
};

const eventOverlapsDay = (
	event: CalendarEvent,
	dayValue: dayjs.Dayjs,
): boolean => {
	const dayStart = dayValue.startOf("day");
	const dayEnd = dayStart.add(1, "day");
	const startAt = event.startAt ?? dayStart;
	const endAt = event?.endAt?.isAfter(startAt)
		? event.endAt
		: startAt.add(1, "minute");
	return startAt.isBefore(dayEnd) && endAt.isAfter(dayStart);
};

const formatEventTimeRange = (
	event: CalendarEvent,
	dayValue: dayjs.Dayjs,
	includeHours: boolean,
	visibleStartMinutes = 0,
	visibleEndMinutes = 24 * 60,
): string => {
	if (!includeHours || !event.startAt) {
		return "";
	}
	const dayStart = dayValue.startOf("day");
	const dayEnd = dayStart.add(1, "day");
	const visibleStart = dayStart.add(visibleStartMinutes, "minute");
	const visibleEnd = dayStart.add(visibleEndMinutes, "minute");
	const start = event.startAt.isBefore(visibleStart)
		? visibleStart
		: event.startAt.isBefore(dayStart)
			? dayStart
			: event.startAt;
	const end = event?.endAt?.isBefore(dayEnd) ? event.endAt : dayEnd;
	const startLabel = start.format("h:mm A");
	if (!end || !end.isValid()) {
		return startLabel;
	}
	const boundedEnd = end.isAfter(visibleEnd) ? visibleEnd : end;
	const clampedEnd = boundedEnd.isSame(dayEnd)
		? dayEnd.subtract(1, "minute")
		: boundedEnd;
	return `${startLabel} - ${clampedEnd.format("h:mm A")}`;
};

const getEventStyle = (event: CalendarEvent, isDarkTheme: boolean) => ({
	backgroundColor:
		event.backColor ||
		(isDarkTheme ? "rgba(96, 165, 250, 0.22)" : "rgba(41, 98, 255, 0.12)"),
	color: event.textColor || "inherit",
	borderColor:
		event.backColor ||
		(isDarkTheme ? "rgba(96, 165, 250, 0.34)" : "rgba(41, 98, 255, 0.28)"),
});

const getTimelinePosition = (
	event: CalendarEvent,
	dayValue: dayjs.Dayjs,
	visibleStartMinutes = 0,
	visibleEndMinutes = 24 * 60,
): { top: number; height: number } => {
	const dayStart = dayValue.startOf("day");
	const dayEnd = dayStart.add(1, "day");
	const start = event?.startAt?.isAfter(dayStart) ? event.startAt : dayStart;
	const rawEnd = event?.endAt?.isBefore(dayEnd) ? event.endAt : dayEnd;
	const end = rawEnd.isAfter(start) ? rawEnd : start.add(30, "minute");
	const startMinutes = Math.max(
		visibleStartMinutes,
		Math.min(visibleEndMinutes, start.diff(dayStart, "minute")),
	);
	const endMinutes = Math.max(
		startMinutes + 1,
		Math.min(visibleEndMinutes, end.diff(dayStart, "minute")),
	);
	const durationMinutes = Math.max(30, endMinutes - startMinutes);
	return {
		top: ((startMinutes - visibleStartMinutes) / 60) * 56,
		height: Math.max(26, (durationMinutes / 60) * 56),
	};
};

const buildTimelineEventLayouts = (
	events: CalendarEvent[],
	dayValue: dayjs.Dayjs,
	visibleStartMinutes = 0,
	visibleEndMinutes = 24 * 60,
): TimelineEventLayout[] => {
	const positionedEvents = events
		.map((event) => {
			const dayStart = dayValue.startOf("day");
			const dayEnd = dayStart.add(1, "day");
			const start =
				event?.startAt?.isAfter(dayStart) || !event.startAt
					? (event.startAt ?? dayStart)
					: dayStart;
			const rawEnd = event?.endAt?.isBefore(dayEnd) ? event.endAt : dayEnd;
			const end = rawEnd?.isAfter(start) ? rawEnd : start.add(30, "minute");
			const startMinutes = start.diff(dayStart, "minute");
			const endMinutes = end.diff(dayStart, "minute");
			if (
				endMinutes <= visibleStartMinutes ||
				startMinutes >= visibleEndMinutes
			) {
				return undefined;
			}
			return {
				event,
				start,
				end,
				...getTimelinePosition(
					event,
					dayValue,
					visibleStartMinutes,
					visibleEndMinutes,
				),
			};
		})
		.filter(
			(
				value,
			): value is {
				event: CalendarEvent;
				start: dayjs.Dayjs;
				end: dayjs.Dayjs;
				top: number;
				height: number;
			} => value !== undefined,
		)
		.sort(
			(a, b) =>
				a.start.valueOf() - b.start.valueOf() ||
				a.end.valueOf() - b.end.valueOf(),
		);

	const layouts: TimelineEventLayout[] = [];
	let clusterStartIndex = 0;

	while (clusterStartIndex < positionedEvents.length) {
		let clusterEnd = positionedEvents[clusterStartIndex].end;
		let clusterStopIndex = clusterStartIndex + 1;
		while (clusterStopIndex < positionedEvents.length) {
			const candidate = positionedEvents[clusterStopIndex];
			if (!candidate.start.isBefore(clusterEnd)) {
				break;
			}
			if (candidate.end.isAfter(clusterEnd)) {
				clusterEnd = candidate.end;
			}
			clusterStopIndex += 1;
		}

		const laneEndTimes: dayjs.Dayjs[] = [];
		const clusterLayouts: TimelineEventLayout[] = [];
		for (let i = clusterStartIndex; i < clusterStopIndex; i += 1) {
			const positionedEvent = positionedEvents[i];
			let laneIndex = laneEndTimes.findIndex(
				(laneEnd) =>
					laneEnd.isSame(positionedEvent.start) ||
					laneEnd.isBefore(positionedEvent.start),
			);
			if (laneIndex === -1) {
				laneIndex = laneEndTimes.length;
				laneEndTimes.push(positionedEvent.end);
			} else {
				laneEndTimes[laneIndex] = positionedEvent.end;
			}
			clusterLayouts.push({
				event: positionedEvent.event,
				top: positionedEvent.top,
				height: positionedEvent.height,
				laneIndex,
				laneCount: 1,
			});
		}

		const laneCount = Math.max(
			1,
			...clusterLayouts.map((layout) => layout.laneIndex + 1),
		);
		clusterLayouts.forEach((layout) => {
			layouts.push({
				...layout,
				laneCount,
			});
		});

		clusterStartIndex = clusterStopIndex;
	}

	return layouts;
};

const eventOverlapsVisibleWindow = (
	event: CalendarEvent,
	dayValue: dayjs.Dayjs,
	visibleStartMinutes = 0,
	visibleEndMinutes = 24 * 60,
): boolean => {
	const dayStart = dayValue.startOf("day");
	const start = event?.startAt?.isAfter(dayStart) ? event.startAt : dayStart;
	const rawEnd = event?.endAt?.isAfter(start)
		? event.endAt
		: start.add(30, "minute");
	const eventStartMinutes = start.diff(dayStart, "minute");
	const eventEndMinutes = rawEnd.diff(dayStart, "minute");
	return (
		eventEndMinutes > visibleStartMinutes &&
		eventStartMinutes < visibleEndMinutes
	);
};

const buildMonthGrid = (anchorDay: dayjs.Dayjs): dayjs.Dayjs[] => {
	const firstGridDay = anchorDay
		.startOf("day")
		.subtract(anchorDay.day(), "day");
	return Array.from({ length: 42 }, (_, index) =>
		firstGridDay.add(index, "day"),
	);
};

const buildWeekDays = (anchorDay: dayjs.Dayjs): dayjs.Dayjs[] => {
	const startOfWeek = anchorDay.startOf("day").subtract(anchorDay.day(), "day");
	return Array.from({ length: 7 }, (_, index) => startOfWeek.add(index, "day"));
};

const shortMonthKeys = [
	"JanShort",
	"FebShort",
	"MarShort",
	"AprShort",
	"MayShort",
	"JunShort",
	"JulShort",
	"AugShort",
	"SepShort",
	"OctShort",
	"NovShort",
	"DecShort",
] as const;

const shortMonthFallbackLabels = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
] as const;

const longMonthKeys = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
] as const;

const longWeekdayKeys = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
] as const;

export const AvailabilityCalendar: React.FC = () => {
	const checkoutFormState = useCheckoutFormState();
	const productDetailsState = useProductDetailsState();
	const siteLayoutState = useSiteLayoutState();
	const storefrontSettingsState = useStorefrontSettingsState();
	const themeState = useThemeState();
	const mediaQueries = useTailwindMediaQueries();
	const translate = useTranslate();

	const product = productDetailsState.get().product;
	const inventoryId = product?.InventoryId;
	const warehouseId = checkoutFormState.get().warehouseIdValue;
	const fromDate = checkoutFormState.get().fromDateValue;
	const toDate = checkoutFormState.get().toDateValue;
	const settings = storefrontSettingsState.get().settings;
	const includeHours = settings?.ShowReservationHourFields ?? false;
	const initialCalendarView = normalizeCalendarView(
		settings?.InitialAvailabilityCalendarView,
	);
	const enableHoursOfOperation = settings?.EnableHoursOfOperation ?? false;
	const enableOpenDays = settings?.EnableOpenDays ?? false;
	const openMinutes = parseTimeToMinutes(settings?.OpenTime);
	const closeMinutes = parseTimeToMinutes(settings?.CloseTime);
	const hasConfiguredHours =
		enableHoursOfOperation &&
		openMinutes !== undefined &&
		closeMinutes !== undefined;
	const hasCompactWeekHours = hasConfiguredHours && openMinutes < closeMinutes;
	const openDayByWeekIndex = useMemo(
		() => [
			settings?.OpenSunday ?? false,
			settings?.OpenMonday ?? false,
			settings?.OpenTuesday ?? false,
			settings?.OpenWednesday ?? false,
			settings?.OpenThursday ?? false,
			settings?.OpenFriday ?? false,
			settings?.OpenSaturday ?? false,
		],
		[settings],
	);
	const themeName = themeState.get().themeName;
	const isDarkTheme = themeName === "dark";
	const isPhone = mediaQueries.phone;
	const showDayStatAbbreviations = mediaQueries.desktop;
	const reservationStartDay = useMemo(
		() =>
			dayjs(fromDate || undefined).isValid()
				? dayjs(fromDate).startOf("day")
				: dayjs().startOf("day"),
		[fromDate],
	);
	const reservationEndDay = useMemo(
		() =>
			dayjs(toDate || undefined).isValid()
				? dayjs(toDate).startOf("day")
				: reservationStartDay,
		[toDate, reservationStartDay],
	);

	const [calendarView, setCalendarView] =
		useState<CalendarView>(initialCalendarView);
	const [visibleMonth, setVisibleMonth] = useState(() => reservationStartDay);
	const [selectedDay, setSelectedDay] = useState(() => reservationStartDay);
	const [events, setEvents] = useState<CalendarEvent[]>([]);
	const [monthDatesByKey, setMonthDatesByKey] = useState<
		Record<string, CalendarDate>
	>({});
	const [isLoading, setIsLoading] = useState(false);
	const monthDays = useMemo(() => buildMonthGrid(visibleMonth), [visibleMonth]);
	const weekDays = useMemo(() => buildWeekDays(selectedDay), [selectedDay]);
	const visibleMonthStart = useMemo(
		() => monthDays[0].startOf("day"),
		[monthDays],
	);
	const visibleMonthEnd = useMemo(
		() => monthDays[monthGridLastIndex].endOf("day"),
		[monthDays],
	);
	const visibleWeekStart = useMemo(
		() => weekDays[0].startOf("day"),
		[weekDays],
	);
	const visibleWeekEnd = useMemo(() => weekDays[6].endOf("day"), [weekDays]);
	const requestStart = useMemo(
		() =>
			calendarView === "day"
				? selectedDay
				: calendarView === "week"
					? visibleWeekStart
					: visibleMonthStart,
		[calendarView, selectedDay, visibleWeekStart, visibleMonthStart],
	);
	const requestEnd = useMemo(
		() =>
			calendarView === "day"
				? selectedDay.endOf("day")
				: calendarView === "week"
					? visibleWeekEnd
					: visibleMonthEnd,
		[calendarView, selectedDay, visibleWeekEnd, visibleMonthEnd],
	);
	const requestFrom = useMemo(
		() =>
			includeHours
				? ensureDateTimeSegment(
						undefined,
						requestStart.format("YYYY-MM-DD"),
						"00:00",
					)
				: requestStart.format("YYYY-MM-DD"),
		[includeHours, requestStart],
	);
	const requestTo = useMemo(
		() =>
			includeHours
				? ensureDateTimeSegment(
						undefined,
						requestEnd.format("YYYY-MM-DD"),
						"23:59",
					)
				: requestEnd.format("YYYY-MM-DD"),
		[includeHours, requestEnd],
	);
	const loadStorefrontSettings = useEffectEvent(async () => {
		await storefrontSettingsState.loadAsync();
	});
	const showSiteError = useEffectEvent((error: unknown) => {
		siteLayoutState.showError(error);
	});

	useEffect(() => {
		void loadStorefrontSettings();
	}, []);

	useEffect(() => {
		setCalendarView((current) =>
			current === initialCalendarView ? current : initialCalendarView,
		);
	}, [initialCalendarView]);

	useEffect(() => {
		setSelectedDay((current) =>
			current === reservationStartDay ? current : reservationStartDay,
		);
		setVisibleMonth((current) =>
			current === reservationStartDay ? current : reservationStartDay,
		);
	}, [reservationStartDay]);

	useEffect(() => {
		if (
			(calendarView === "week" || calendarView === "day") &&
			selectedDay !== visibleMonth
		) {
			setVisibleMonth(selectedDay);
		}
	}, [calendarView, selectedDay, visibleMonth]);

	useEffect(() => {
		const loadCalendarAsync = async () => {
			if (!inventoryId || !warehouseId) {
				setEvents([]);
				setMonthDatesByKey({});
				return;
			}

			setIsLoading(true);
			try {
				const response =
					await storefrontSdk.storefrontV1GetAvailabilityCalendarAsync(
						inventoryId,
						warehouseId,
						requestFrom,
						requestTo,
						{
							params: {
								IncludeHours: includeHours,
							},
						},
					);
				const data = response.data as InventoryAvailabilityCalendarResponseDto;
				if (calendarView === "month") {
					const nextMonthDatesByKey = (data.Dates ?? [])
						.map(normalizeDate)
						.filter((value): value is CalendarDate => value !== undefined)
						.reduce<Record<string, CalendarDate>>((acc, value) => {
							acc[value.dayKey] = value;
							return acc;
						}, {});
					setMonthDatesByKey(nextMonthDatesByKey);
					setEvents([]);
				} else {
					setEvents(
						(data.InventoryAvailabilityCalendarEvents ?? []).map(
							normalizeEvent,
						),
					);
				}
			} catch (error) {
				setEvents([]);
				setMonthDatesByKey({});
				showSiteError(error);
			} finally {
				setIsLoading(false);
			}
		};

		void loadCalendarAsync();
	}, [
		inventoryId,
		calendarView,
		requestFrom,
		requestTo,
		includeHours,
		warehouseId,
	]);
	const selectedDayEvents = events
		.filter((event) => eventOverlapsDay(event, selectedDay))
		.sort((a, b) => (a.startAt?.valueOf() ?? 0) - (b.startAt?.valueOf() ?? 0));
	const weekEventsByDay = weekDays.reduce<Record<string, CalendarEvent[]>>(
		(acc, dayValue) => {
			const dayKey = dayValue.format("YYYY-MM-DD");
			acc[dayKey] = events
				.filter((event) => eventOverlapsDay(event, dayValue))
				.sort(
					(a, b) => (a.startAt?.valueOf() ?? 0) - (b.startAt?.valueOf() ?? 0),
				);
			return acc;
		},
		{},
	);

	const dayNames = [
		translate("Sun"),
		translate("Mon"),
		translate("Tue"),
		translate("Wed"),
		translate("Thu"),
		translate("Fri"),
		translate("Sat"),
	];
	const getShortMonthLabel = (value: dayjs.Dayjs) => {
		const monthIndex = value.month();
		const translated = translate(shortMonthKeys[monthIndex]);
		if (translated === shortMonthKeys[monthIndex]) {
			return shortMonthFallbackLabels[monthIndex];
		}
		return translated;
	};
	const getLongMonthLabel = (value: dayjs.Dayjs) =>
		translate(longMonthKeys[value.month()]);
	const getLongWeekdayLabel = (value: dayjs.Dayjs) =>
		translate(longWeekdayKeys[value.day()]);
	const today = dayjs().startOf("day");
	//const surface = isDarkTheme ? "rgba(15, 23, 42, 0.95)" : "#ffffff";
	const surface = "transparent";
	const surfaceMuted = isDarkTheme
		? "rgba(51, 65, 85, 0.55)"
		: "rgba(226, 232, 240, 0.7)";
	const borderColor = isDarkTheme
		? "rgba(100, 116, 139, 0.45)"
		: "rgba(148, 163, 184, 0.35)";
	const borderSoft = isDarkTheme
		? "rgba(71, 85, 105, 0.8)"
		: "rgba(226, 232, 240, 0.95)";
	const textPrimary = isDarkTheme ? "#e2e8f0" : "#0f172a";
	const textMuted = isDarkTheme ? "#94a3b8" : "#64748b";
	const accentStrong = isDarkTheme ? "#e2e8f0" : "#0f172a";
	const accentSoft = isDarkTheme
		? "rgba(96, 165, 250, 0.16)"
		: "rgba(59, 130, 246, 0.08)";
	const selectedSoft = isDarkTheme
		? "rgba(148, 163, 184, 0.14)"
		: "rgba(15, 23, 42, 0.08)";
	const reservationBorder =
		"var(--availability-calendar-reservation-date-border)";
	const isShopOpenOnDay = (dayValue: dayjs.Dayjs) => {
		if (!enableOpenDays) {
			return true;
		}
		return openDayByWeekIndex[dayValue.day()] ?? false;
	};
	const isWithinOperatingHours = (timeInMinutes: number) => {
		if (!hasConfiguredHours) {
			return true;
		}
		if (openMinutes <= closeMinutes) {
			return timeInMinutes >= openMinutes && timeInMinutes <= closeMinutes;
		}
		return timeInMinutes >= openMinutes || timeInMinutes <= closeMinutes;
	};
	const isShopOpenAtHour = (dayValue: dayjs.Dayjs, hour: number) =>
		isShopOpenOnDay(dayValue) && isWithinOperatingHours(hour * 60);
	const weekVisibleDays = useMemo(
		() =>
			enableOpenDays
				? weekDays.filter(
						(dayValue) => openDayByWeekIndex[dayValue.day()] ?? false,
					)
				: weekDays,
		[enableOpenDays, openDayByWeekIndex, weekDays],
	);
	const weekVisibleHours = useMemo(() => {
		if (
			!hasCompactWeekHours ||
			openMinutes === undefined ||
			closeMinutes === undefined
		) {
			return hoursInDay;
		}
		return hoursInDay.filter((hour) => {
			const slotStart = hour * 60;
			const slotEnd = slotStart + 60;
			return slotStart < closeMinutes && slotEnd > openMinutes;
		});
	}, [hasCompactWeekHours, openMinutes, closeMinutes]);
	const weekVisibleStartMinutes =
		hasCompactWeekHours && openMinutes !== undefined ? openMinutes : 0;
	const weekVisibleEndMinutes =
		hasCompactWeekHours && closeMinutes !== undefined ? closeMinutes : 24 * 60;
	const weekTimelineHeight =
		((weekVisibleEndMinutes - weekVisibleStartMinutes) / 60) * 56;
	const dayVisibleHours = weekVisibleHours;
	const dayTimelineHeight = weekTimelineHeight;
	const selectedDayEventLayouts = useMemo(
		() =>
			buildTimelineEventLayouts(
				selectedDayEvents,
				selectedDay,
				weekVisibleStartMinutes,
				weekVisibleEndMinutes,
			),
		[
			selectedDayEvents,
			selectedDay,
			weekVisibleStartMinutes,
			weekVisibleEndMinutes,
		],
	);
	const weekEventLayoutsByVisibleDay = useMemo(
		() =>
			weekVisibleDays.reduce<Record<string, TimelineEventLayout[]>>(
				(acc, dayValue) => {
					const dayKey = dayValue.format("YYYY-MM-DD");
					acc[dayKey] = buildTimelineEventLayouts(
						weekEventsByDay[dayKey] ?? [],
						dayValue,
						weekVisibleStartMinutes,
						weekVisibleEndMinutes,
					);
					return acc;
				},
				{},
			),
		[
			weekVisibleDays,
			weekEventsByDay,
			weekVisibleStartMinutes,
			weekVisibleEndMinutes,
		],
	);
	const hasOverlappingVisibleWeekEvents = useMemo(
		() =>
			Object.values(weekEventLayoutsByVisibleDay).some((layouts) =>
				layouts.some((layout) => layout.laneCount > 1),
			),
		[weekEventLayoutsByVisibleDay],
	);
	const weekVisibleEventsByDay = useMemo(
		() =>
			weekVisibleDays.reduce<Record<string, CalendarEvent[]>>(
				(acc, dayValue) => {
					const dayKey = dayValue.format("YYYY-MM-DD");
					acc[dayKey] = (weekEventsByDay[dayKey] ?? []).filter((event) =>
						eventOverlapsVisibleWindow(
							event,
							dayValue,
							weekVisibleStartMinutes,
							weekVisibleEndMinutes,
						),
					);
					return acc;
				},
				{},
			),
		[
			weekVisibleDays,
			weekEventsByDay,
			weekVisibleStartMinutes,
			weekVisibleEndMinutes,
		],
	);
	const getClosedDaySurface = () =>
		isDarkTheme ? "rgba(51, 65, 85, 0.42)" : "rgba(226, 232, 240, 0.95)";
	const getClosedHourSurface = () =>
		isDarkTheme ? "rgba(15, 23, 42, 0.4)" : "rgba(148, 163, 184, 0.14)";
	const isPrevDayDisabled = !selectedDay.isAfter(today, "day");
	const isPrevWeekDisabled = !visibleWeekStart.isAfter(today, "day");
	const navigationLabel =
		calendarView === "month"
			? `${getShortMonthLabel(monthDays[0])} ${monthDays[0].format("D")} - ${getShortMonthLabel(monthDays[monthGridLastIndex])} ${monthDays[monthGridLastIndex].format("D, YYYY")}`
			: calendarView === "week"
				? `${getShortMonthLabel(weekDays[0])} ${weekDays[0].format("D")} - ${getShortMonthLabel(weekDays[6])} ${weekDays[6].format("D, YYYY")}`
				: `${getLongWeekdayLabel(selectedDay)}, ${getLongMonthLabel(selectedDay)} ${selectedDay.format("D, YYYY")}`;

	if (!product?.InventoryId || !warehouseId || !fromDate || !toDate) {
		return null;
	}

	return (
		<section
			css={css`
				/*margin: 1.25rem 0 0;*/
				overflow: hidden;
			`}
		>
			<div
				css={css`
					display: flex;
					flex-wrap: wrap;
					align-items: center;
					justify-content: space-between;
					gap: 0.75rem;
					padding: 0.25rem;
					border-bottom: 1px solid ${borderColor};
				`}
			>
				<div>
					<div
						css={css`
							display: flex;
							align-items: center;
							gap: 0.5rem;
							font-size: 1rem;
							font-weight: 700;
							color: ${textPrimary};
						`}
					>
						{/* <CalendarDays size={18} />
						{translate("Availability Calendar")} */}
						<ReservationDates className="max-w-160" />
					</div>
				</div>
				<div
					css={css`
						display: flex;
						flex-wrap: wrap;
						align-items: center;
						gap: 0.5rem;
					`}
				>
					<div
						css={css`
							display: inline-flex;
							background: ${surfaceMuted};
							padding: 0.2rem;
							border-radius: 999px;
						`}
					>
						{calendarViews.map((view) => (
							<button
								key={view}
								type="button"
								onClick={() => setCalendarView(view)}
								css={css`
									border: 0;
									border-radius: 999px;
									padding: 0.45rem 0.8rem;
									background: ${calendarView === view ? accentStrong : "transparent"};
									color: ${calendarView === view ? (isDarkTheme ? "#020617" : "#ffffff") : textMuted};
									font-size: 0.82rem;
									font-weight: 700;
									text-transform: capitalize;
									cursor: pointer;
								`}
							>
								{view === "month" ? "Month" : view === "week" ? "Week" : "Day"}
							</button>
						))}
					</div>
					<div
						css={css`
							display: flex;
							align-items: center;
							gap: 0.25rem;
						`}
					>
						<button
							type="button"
							disabled={
								calendarView === "day"
									? isPrevDayDisabled
									: calendarView === "week"
										? isPrevWeekDisabled
										: false
							}
							onClick={() => {
								if (calendarView === "month") {
									setVisibleMonth((current) => current.subtract(1, "month"));
									return;
								}
								if (calendarView === "week") {
									setSelectedDay((current) => current.subtract(7, "day"));
									return;
								}
								setSelectedDay((current) => current.subtract(1, "day"));
							}}
							css={css`
								display: inline-flex;
								align-items: center;
								justify-content: center;
								width: 2rem;
								height: 2rem;
								border: 1px solid ${borderColor};
								border-radius: 999px;
								background: ${surface};
								color: ${textPrimary};
								cursor: ${
									calendarView === "day"
										? isPrevDayDisabled
											? "not-allowed"
											: "pointer"
										: calendarView === "week"
											? isPrevWeekDisabled
												? "not-allowed"
												: "pointer"
											: "pointer"
								};
								opacity: ${
									calendarView === "day"
										? isPrevDayDisabled
											? 0.45
											: 1
										: calendarView === "week"
											? isPrevWeekDisabled
												? 0.45
												: 1
											: 1
								};
							`}
							aria-label={
								calendarView === "month"
									? "Previous month"
									: calendarView === "week"
										? "Previous week"
										: "Previous day"
							}
						>
							<ChevronLeft size={16} />
						</button>
						<div
							css={css`
								min-width: 8rem;
								text-align: center;
								font-weight: 700;
								color: ${textPrimary};
							`}
						>
							{navigationLabel}
						</div>
						<button
							type="button"
							onClick={() => {
								if (calendarView === "month") {
									setVisibleMonth((current) => current.add(1, "month"));
									return;
								}
								if (calendarView === "week") {
									setSelectedDay((current) => current.add(7, "day"));
									return;
								}
								setSelectedDay((current) => current.add(1, "day"));
							}}
							css={css`
								display: inline-flex;
								align-items: center;
								justify-content: center;
								width: 2rem;
								height: 2rem;
								border: 1px solid ${borderColor};
								border-radius: 999px;
								background: ${surface};
								color: ${textPrimary};
								cursor: pointer;
							`}
							aria-label={
								calendarView === "month"
									? "Next month"
									: calendarView === "week"
										? "Next week"
										: "Next day"
							}
						>
							<ChevronRight size={16} />
						</button>
					</div>
				</div>
			</div>

			<div css={css`padding: 1rem;`}>
				{calendarView === "month" && (
					<div
						css={css`
							display: flex;
							flex-wrap: wrap;
							align-items: center;
							gap: 0.5rem 0.75rem;
							margin-bottom: 0.85rem;
							font-size: 0.78rem;
							color: ${textMuted};
						`}
					>
						<div
							css={css`
								display: inline-flex;
								align-items: center;
								gap: 0.35rem;
							`}
						>
							<span
								css={css`
									display: inline-flex;
									align-items: center;
									justify-content: center;
									min-width: 1.4rem;
									padding: 0.12rem 0.3rem;
									border: 1px solid ${borderSoft};
									border-radius: 999px;
									background: ${
										isDarkTheme
											? "rgba(22, 163, 74, 0.14)"
											: "rgba(34, 197, 94, 0.08)"
									};
									color: ${textPrimary};
									font-weight: 700;
								`}
							>
								A
							</span>
							<span>{translate("Available")}</span>
						</div>
						{includeHours && (
							<div
								css={css`
									margin-top: 0rem;
									font-size: 0.8rem;
									color: ${textMuted};
								`}
							>
								{translate("Click on a day to see hourly availability.")}
							</div>
						)}
					</div>
				)}

				{isLoading && (
					<div
						css={css`
							padding: 0.75rem 0 1rem;
							font-size: 0.9rem;
							color: ${textMuted};
						`}
					>
						{translate("Loading")}...
					</div>
				)}

				{calendarView === "week" &&
					includeHours &&
					!isPhone &&
					hasOverlappingVisibleWeekEvents && (
						<div
							css={css`
								padding: 0 0 0.85rem;
								font-size: 0.82rem;
								color: ${textMuted};
							`}
						>
							{translate(
								"Week view switched to the readable event list because overlapping hourly events were detected.",
							)}
						</div>
					)}

				{calendarView === "week" && weekVisibleDays.length === 0 && (
					<div
						css={css`
							padding: 1rem 0;
							font-size: 0.9rem;
							color: ${textMuted};
						`}
					>
						{translate("The shop is closed for the selected week.")}
					</div>
				)}

				{calendarView === "month" && (
					<div>
						<div
							css={css`
								display: grid;
								grid-template-columns: repeat(7, minmax(0, 1fr));
								gap: ${isPhone ? "0.25rem" : "0.5rem"};
								margin-bottom: ${isPhone ? "0.35rem" : "0.5rem"};
							`}
						>
							{dayNames.map((dayName) => (
								<div
									key={dayName}
									css={css`
										font-size: 0.75rem;
										font-weight: 700;
										text-transform: uppercase;
										letter-spacing: 0.04em;
										color: ${textMuted};
										padding: 0 0.25rem;
									`}
								>
									{dayName}
								</div>
							))}
						</div>
						<div
							css={css`
								display: grid;
								grid-template-columns: repeat(7, minmax(0, 1fr));
								gap: ${isPhone ? "0.25rem" : "0.5rem"};
							`}
						>
							{monthDays.map((dayValue) => {
								const dayDateSummary =
									monthDatesByKey[dayValue.format("YYYY-MM-DD")];
								const isSelected = dayValue.isSame(selectedDay, "day");
								const isPastDay = dayValue.isBefore(today, "day");
								const isClosedDay = !isShopOpenOnDay(dayValue);
								const isDayDisabled = isPastDay || isClosedDay;
								const isReservationRange =
									!dayValue.isBefore(reservationStartDay, "day") &&
									!dayValue.isAfter(reservationEndDay, "day");
								const isReservationBoundary =
									dayValue.isSame(reservationStartDay, "day") ||
									dayValue.isSame(reservationEndDay, "day");
								return (
									<button
										key={dayValue.format("YYYY-MM-DD")}
										type="button"
										disabled={isDayDisabled}
										onClick={() => {
											setSelectedDay(dayValue.startOf("day"));
											setCalendarView("day");
										}}
										css={css`
											min-height: ${isPhone ? "5rem" : "6rem"};
											display: flex;
											flex-direction: column;
											justify-content: flex-start;
											padding: ${isPhone ? "0.35rem" : "0.55rem"};
											border-radius: 14px;
											border: ${isReservationBoundary ? "2px" : "1px"} solid
												${
													isSelected
														? accentStrong
														: isDayDisabled
															? borderSoft
															: isReservationRange
																? reservationBorder
																: borderSoft
												};
											background: ${
												isSelected
													? selectedSoft
													: isDayDisabled
														? getClosedDaySurface()
														: isReservationRange
															? accentSoft
															: surface
											};
											box-shadow: ${
												isReservationRange
													? `inset 0 0 0 1px ${reservationBorder}`
													: "none"
											};
											text-align: left;
											cursor: ${isDayDisabled ? "not-allowed" : "pointer"};
											opacity: ${isPastDay ? 0.35 : isClosedDay ? 0.55 : 1};
										`}
									>
										<div
											css={css`
												display: flex;
												align-items: center;
												justify-content: space-between;
												margin-bottom: ${isPhone ? "0.28rem" : "0.45rem"};
											`}
										>
											<span
												css={css`
													font-size: 0.68rem;
													font-weight: ${dayValue.date() === 1 ? 800 : 600};
													color: ${dayValue.date() === 1 ? textPrimary : textMuted};
													text-transform: uppercase;
													letter-spacing: 0.04em;
												`}
											>
												{getShortMonthLabel(dayValue)}
											</span>
											<span
												css={css`
													font-size: 0.78rem;
													font-weight: ${dayValue.date() === 1 ? 700 : 400};
													color: ${textPrimary};
												`}
											>
												{dayValue.format("D")}
											</span>
										</div>
										<div
											css={css`
												display: grid;
												gap: ${isPhone ? "0.2rem" : "0.28rem"};
											`}
										>
											{dayDateSummary && (
												<div
													css={css`
														display: flex;
														align-items: center;
														justify-content: ${
															!showDayStatAbbreviations
																? "center"
																: "space-between"
														};
														gap: ${showDayStatAbbreviations ? "0.35rem" : "0"};
														padding: ${
															isPhone ? "0.18rem 0.26rem" : "0.28rem 0.38rem"
														};
														border: 1px solid ${borderSoft};
														border-radius: 8px;
														font-size: ${isPhone ? "0.62rem" : "0.68rem"};
														line-height: 1.1;
														overflow: hidden;
														white-space: nowrap;
														background: ${
															isDarkTheme
																? "rgba(22, 163, 74, 0.14)"
																: "rgba(34, 197, 94, 0.08)"
														};
														color: ${textPrimary};
													`}
												>
													{showDayStatAbbreviations && (
														<span
															css={css`
																font-weight: 700;
																letter-spacing: 0.02em;
															`}
														>
															A
														</span>
													)}
													<span>{dayDateSummary.Available ?? 0}</span>
												</div>
											)}
										</div>
									</button>
								);
							})}
						</div>
					</div>
				)}

				{calendarView === "week" &&
					weekVisibleDays.length > 0 &&
					includeHours &&
					!isPhone &&
					!hasOverlappingVisibleWeekEvents && (
						<div
							css={css`
							display: grid;
							grid-template-columns: 4.5rem repeat(${weekVisibleDays.length}, minmax(0, 1fr));
							gap: 0.75rem;
							align-items: start;
						`}
						>
							<div
								css={css`
								padding-top: 4.5rem;
								display: grid;
								grid-template-rows: repeat(${weekVisibleHours.length}, 56px);
							`}
							>
								{weekVisibleHours.map((hour) => (
									<div
										key={`week-hour-label-${hour}`}
										css={css`
										position: relative;
										font-size: 0.75rem;
										color: ${textMuted};
									`}
									>
										<div
											css={css`
											position: absolute;
											top: -0.42rem;
											right: 0.5rem;
										`}
										>
											{dayjs().hour(hour).minute(0).format("h A")}
										</div>
									</div>
								))}
							</div>
							{weekVisibleDays.map((dayValue) => {
								const dayEventLayouts =
									weekEventLayoutsByVisibleDay[dayValue.format("YYYY-MM-DD")] ??
									[];
								const isSelected = dayValue.isSame(selectedDay, "day");
								const isPastDay = dayValue.isBefore(today, "day");
								const isClosedDay = !isShopOpenOnDay(dayValue);
								const isDayDisabled = isPastDay || isClosedDay;
								const isReservationRange =
									!dayValue.isBefore(reservationStartDay, "day") &&
									!dayValue.isAfter(reservationEndDay, "day");
								const isReservationBoundary =
									dayValue.isSame(reservationStartDay, "day") ||
									dayValue.isSame(reservationEndDay, "day");
								return (
									<button
										key={dayValue.format("YYYY-MM-DD")}
										type="button"
										disabled={isDayDisabled}
										onClick={() => {
											setSelectedDay(dayValue.startOf("day"));
											setCalendarView("day");
										}}
										css={css`
										border: ${isReservationBoundary ? "2px" : "1px"} solid
											${
												isSelected
													? accentStrong
													: isReservationRange
														? reservationBorder
														: borderSoft
											};
										border-radius: 16px;
										background: ${
											isSelected
												? selectedSoft
												: isClosedDay
													? getClosedDaySurface()
													: surface
										};
										padding: 0.75rem;
										text-align: left;
										cursor: ${isDayDisabled ? "not-allowed" : "pointer"};
										overflow: hidden;
										box-shadow: ${
											isReservationRange
												? `inset 0 0 0 1px ${reservationBorder}`
												: "none"
										};
										opacity: ${isPastDay ? 0.72 : isClosedDay ? 0.8 : 1};
									`}
									>
										<div
											css={css`
											height: 3rem;
											margin-bottom: 0.75rem;
											display: flex;
											flex-direction: column;
											justify-content: flex-start;
										`}
										>
											<div
												css={css`
												font-size: 0.72rem;
												font-weight: 700;
												text-transform: uppercase;
												letter-spacing: 0.04em;
												color: ${textMuted};
											`}
											>
												{getLongWeekdayLabel(dayValue)}
											</div>
											<div
												css={css`
												font-size: 1rem;
												font-weight: 700;
												color: ${textPrimary};
												margin-top: 0.15rem;
											`}
											>
												{`${getShortMonthLabel(dayValue)} ${dayValue.format("D")}`}
											</div>
										</div>
										<div
											css={css`
											position: relative;
											min-height: ${weekTimelineHeight}px;
											border: 1px solid ${borderSoft};
											border-radius: 14px;
											background:
												repeating-linear-gradient(
													to bottom,
													${surface} 0,
													${surface} 55px,
													${borderSoft} 55px,
													${borderSoft} 56px
												);
											opacity: ${isPastDay ? 0.72 : 1};
										`}
										>
											{weekVisibleHours.map((hour, hourIndex) => {
												const slotTime = dayValue
													.startOf("day")
													.add(hour, "hour");
												const isPastSlot = slotTime
													.endOf("hour")
													.isBefore(dayjs());
												const isClosedHour = !isShopOpenAtHour(dayValue, hour);
												return (
													<div
														key={`${dayValue.format("YYYY-MM-DD")}-hour-${hour}`}
														css={css`
														position: absolute;
														left: 0;
														right: 0;
														top: ${hourIndex * 56}px;
														height: 56px;
														background: ${
															isPastSlot
																? isDarkTheme
																	? "rgba(15, 23, 42, 0.2)"
																	: "rgba(148, 163, 184, 0.08)"
																: isClosedHour
																	? getClosedHourSurface()
																	: "transparent"
														};
													`}
													/>
												);
											})}
											{dayEventLayouts.map((layout, index) => {
												const laneGap = 6;
												const totalHorizontalInset = 12;
												const width = `calc((100% - ${totalHorizontalInset}px - ${(layout.laneCount - 1) * laneGap}px) / ${layout.laneCount})`;
												const left = `calc(6px + ${layout.laneIndex} * (${width} + ${laneGap}px))`;
												return (
													<div
														key={`${layout.event.id ?? layout.event.text ?? "event"}-${index}`}
														style={{
															...getEventStyle(layout.event, isDarkTheme),
															top: `${layout.top}px`,
															height: `${layout.height}px`,
															left,
															width,
														}}
														css={css`
														position: absolute;
														border: 1px solid transparent;
														border-radius: 10px;
														padding: 0.45rem 0.5rem;
														box-shadow: ${
															isDarkTheme
																? "0 8px 18px rgba(2, 6, 23, 0.3)"
																: "0 8px 18px rgba(15, 23, 42, 0.08)"
														};
													`}
													>
														<div
															css={css`
														display: flex;
														flex-direction: column;
														gap: 0.15rem;
														font-size: 0.72rem;
														line-height: 1.25;
														min-width: 0;
														text-align: center;
													`}
														>
															{eventTextToLines(layout.event.text).map(
																(line) => (
																	<div key={line}>{line}</div>
																),
															)}
														</div>
													</div>
												);
											})}
										</div>
									</button>
								);
							})}
						</div>
					)}

				{calendarView === "week" &&
					weekVisibleDays.length > 0 &&
					(!includeHours || isPhone || hasOverlappingVisibleWeekEvents) && (
						<div
							css={css`
							display: grid;
							grid-template-columns: repeat(${isPhone ? 1 : weekVisibleDays.length}, minmax(0, 1fr));
							gap: 0.75rem;
						`}
						>
							{weekVisibleDays.map((dayValue) => {
								const dayEvents =
									weekVisibleEventsByDay[dayValue.format("YYYY-MM-DD")] ?? [];
								const isSelected = dayValue.isSame(selectedDay, "day");
								const isPastDay = dayValue.isBefore(today, "day");
								const isClosedDay = !isShopOpenOnDay(dayValue);
								const isDayDisabled = isPastDay || isClosedDay;
								const isReservationRange =
									!dayValue.isBefore(reservationStartDay, "day") &&
									!dayValue.isAfter(reservationEndDay, "day");
								const isReservationBoundary =
									dayValue.isSame(reservationStartDay, "day") ||
									dayValue.isSame(reservationEndDay, "day");
								return (
									<button
										key={dayValue.format("YYYY-MM-DD")}
										type="button"
										disabled={isDayDisabled}
										onClick={() => {
											setSelectedDay(dayValue.startOf("day"));
											setCalendarView("day");
										}}
										css={css`
										border: ${isReservationBoundary ? "2px" : "1px"} solid
											${
												isSelected
													? accentStrong
													: isReservationRange
														? reservationBorder
														: borderSoft
											};
										border-radius: 16px;
										background: ${
											isSelected
												? selectedSoft
												: isClosedDay
													? getClosedDaySurface()
													: surface
										};
										padding: 0.85rem;
										text-align: left;
										cursor: ${isDayDisabled ? "not-allowed" : "pointer"};
										min-height: 16rem;
										display: flex;
										flex-direction: column;
										box-shadow: ${
											isReservationRange
												? `inset 0 0 0 1px ${reservationBorder}`
												: "none"
										};
									`}
									>
										<div
											css={css`
											height: 3rem;
											margin-bottom: 0.75rem;
										`}
										>
											<div
												css={css`
												font-size: 0.72rem;
												font-weight: 700;
												text-transform: uppercase;
												letter-spacing: 0.04em;
												color: ${textMuted};
											`}
											>
												{getLongWeekdayLabel(dayValue)}
											</div>
											<div
												css={css`
												font-size: 1rem;
												font-weight: 700;
												color: ${textPrimary};
												margin-top: 0.15rem;
											`}
											>
												{`${getShortMonthLabel(dayValue)} ${dayValue.format("D")}`}
											</div>
										</div>
										<div
											css={css`
											display: grid;
											gap: 0.5rem;
											flex: 1 1 auto;
											opacity: ${isPastDay ? 0.72 : isClosedDay ? 0.8 : 1};
										`}
										>
											{dayEvents.map((event, index) => (
												<div
													key={`${event.id ?? event.text ?? "event"}-${index}`}
													style={getEventStyle(event, isDarkTheme)}
													css={css`
													border: 1px solid transparent;
													border-radius: 12px;
													padding: 0.65rem 0.75rem;
												`}
												>
													<div
														css={css`
														font-size: 0.74rem;
														font-weight: 700;
														margin-bottom: 0.25rem;
													`}
													>
														{formatEventTimeRange(
															event,
															dayValue,
															includeHours,
															weekVisibleStartMinutes,
															weekVisibleEndMinutes,
														) || translate("All day")}
													</div>
													<div
														css={css`
														display: flex;
														flex-direction: column;
														gap: 0.18rem;
														font-size: 0.78rem;
														line-height: 1.3;
													`}
													>
														{eventTextToLines(event.text).map((line) => (
															<div key={line}>{line}</div>
														))}
													</div>
												</div>
											))}
											{dayEvents.length === 0 && !isLoading && (
												<div
													css={css`
													font-size: 0.8rem;
													color: ${textMuted};
												`}
												>
													{translate("No availability events for this day.")}
												</div>
											)}
										</div>
									</button>
								);
							})}
						</div>
					)}

				{calendarView === "day" && (
					<div>
						<div
							css={css`
								display: flex;
								align-items: center;
								justify-content: space-between;
								gap: 0.75rem;
								margin-bottom: 1rem;
								flex-wrap: wrap;
							`}
						>
							<div>
								<div
									css={css`
										font-size: 1rem;
										font-weight: 700;
										color: ${textPrimary};
										display: inline-flex;
										align-items: center;
										gap: 0.5rem;
									`}
								>
									{`${getLongWeekdayLabel(selectedDay)}, ${getLongMonthLabel(selectedDay)} ${selectedDay.format("D, YYYY")}`}
									{!selectedDay.isBefore(reservationStartDay, "day") &&
										!selectedDay.isAfter(reservationEndDay, "day") && (
											<span
												css={css`
													display: inline-block;
													width: 0.7rem;
													height: 0.7rem;
													border-radius: 999px;
													background: ${reservationBorder};
													box-shadow: 0 0 0 2px ${surface};
												`}
											/>
										)}
								</div>
								<div
									css={css`
										font-size: 0.84rem;
										color: ${textMuted};
										margin-top: 0.2rem;
									`}
								>
									{includeHours
										? translate("Hour detail is enabled for this storefront.")
										: translate(
												"Hour detail is hidden by storefront settings.",
											)}
								</div>
							</div>
							<div
								css={css`
									display: flex;
									align-items: center;
									gap: 0.5rem;
								`}
							>
								<button
									type="button"
									disabled={isPrevDayDisabled}
									onClick={() =>
										setSelectedDay((current) => current.subtract(1, "day"))
									}
									css={css`
										border: 1px solid ${borderColor};
										border-radius: 999px;
										background: ${surface};
										color: ${textPrimary};
										padding: 0.35rem 0.7rem;
										cursor: ${isPrevDayDisabled ? "not-allowed" : "pointer"};
										opacity: ${isPrevDayDisabled ? 0.45 : 1};
									`}
								>
									{translate("Previous")}
								</button>
								<button
									type="button"
									onClick={() =>
										setSelectedDay((current) => current.add(1, "day"))
									}
									css={css`
										border: 1px solid ${borderColor};
										border-radius: 999px;
										background: ${surface};
										color: ${textPrimary};
										padding: 0.35rem 0.7rem;
										cursor: pointer;
									`}
								>
									{translate("Next")}
								</button>
							</div>
						</div>

						{includeHours && (
							<div
								css={css`
									display: grid;
									grid-template-columns: 4.5rem minmax(0, 1fr);
									gap: 0.75rem;
									align-items: start;
								`}
							>
								<div
									css={css`
										display: grid;
										grid-template-rows: repeat(${dayVisibleHours.length}, 56px);
									`}
								>
									{dayVisibleHours.map((hour) => (
										<div
											key={`day-hour-label-${hour}`}
											css={css`
												position: relative;
												font-size: 0.75rem;
												color: ${textMuted};
											`}
										>
											<div
												css={css`
													position: absolute;
													top: -0.42rem;
													right: 0.5rem;
												`}
											>
												{dayjs().hour(hour).minute(0).format("h A")}
											</div>
										</div>
									))}
								</div>
								<div
									css={css`
										position: relative;
										min-height: ${dayTimelineHeight}px;
										border: 1px solid ${borderSoft};
										border-radius: 16px;
										background:
											repeating-linear-gradient(
												to bottom,
												${!isShopOpenOnDay(selectedDay) ? getClosedDaySurface() : surface} 0,
												${!isShopOpenOnDay(selectedDay) ? getClosedDaySurface() : surface} 55px,
												${borderSoft} 55px,
												${borderSoft} 56px
											);
										opacity: ${
											!isShopOpenOnDay(selectedDay)
												? 0.8
												: selectedDay.isBefore(today, "day")
													? 0.72
													: 1
										};
									`}
								>
									{dayVisibleHours.map((hour, hourIndex) => {
										const slotTime = selectedDay
											.startOf("day")
											.add(hour, "hour");
										const isPastSlot = slotTime.endOf("hour").isBefore(dayjs());
										const isClosedHour = !isShopOpenAtHour(selectedDay, hour);
										return (
											<div
												key={`selected-day-hour-${hour}`}
												css={css`
													position: absolute;
													left: 0;
													right: 0;
													top: ${hourIndex * 56}px;
													height: 56px;
													background: ${
														isPastSlot
															? isDarkTheme
																? "rgba(15, 23, 42, 0.2)"
																: "rgba(148, 163, 184, 0.08)"
															: isClosedHour
																? getClosedHourSurface()
																: "transparent"
													};
												`}
											/>
										);
									})}
									{selectedDayEventLayouts.map((layout, index) => {
										const laneGap = 8;
										const totalHorizontalInset = 20;
										const width = `calc((100% - ${totalHorizontalInset}px - ${(layout.laneCount - 1) * laneGap}px) / ${layout.laneCount})`;
										const left = `calc(10px + ${layout.laneIndex} * (${width} + ${laneGap}px))`;
										return (
											<div
												key={`${layout.event.id ?? layout.event.text ?? "event"}-${index}`}
												style={{
													...getEventStyle(layout.event, isDarkTheme),
													top: `${layout.top}px`,
													height: `${layout.height}px`,
													left,
													width,
												}}
												css={css`
													position: absolute;
													border: 1px solid transparent;
													border-radius: 12px;
													padding: 0.5rem 0.65rem;
													overflow: hidden;
													box-shadow: ${
														isDarkTheme
															? "0 8px 18px rgba(2, 6, 23, 0.3)"
															: "0 8px 18px rgba(15, 23, 42, 0.08)"
													};
												`}
											>
												<div
													css={css`
														display: flex;
														align-items: center;
														gap: 0.35rem;
														font-size: 0.72rem;
														font-weight: 700;
														margin-bottom: 0.2rem;
													`}
												>
													<Clock3 size={12} />
													{formatEventTimeRange(
														layout.event,
														selectedDay,
														includeHours,
														weekVisibleStartMinutes,
														weekVisibleEndMinutes,
													)}
												</div>
												<div
													css={css`
														display: flex;
														flex-direction: column;
														gap: 0.18rem;
														font-size: 0.78rem;
														line-height: 1.3;
														min-width: 0;
													`}
												>
													{eventTextToLines(layout.event.text).map((line) => (
														<div
															key={line}
															css={css`
																overflow: hidden;
																text-overflow: ellipsis;
																white-space: nowrap;
															`}
														>
															{line}
														</div>
													))}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}

						{!includeHours && (
							<div
								css={css`
									display: grid;
									gap: 0.75rem;
								`}
							>
								{selectedDayEvents.map((event, index) => (
									<div
										key={`${event.id ?? event.text ?? "event"}-${index}`}
										style={getEventStyle(event, isDarkTheme)}
										css={css`
											border: 1px solid transparent;
											border-radius: 14px;
											padding: 0.8rem 0.95rem;
										`}
									>
										<div
											css={css`
												font-size: 0.78rem;
												font-weight: 700;
												margin-bottom: 0.3rem;
											`}
										>
											{formatEventTimeRange(event, selectedDay, true) ||
												translate("All day")}
										</div>
										<div
											css={css`
												display: flex;
												flex-direction: column;
												gap: 0.2rem;
											`}
										>
											{eventTextToLines(event.text).map((line) => (
												<div key={line}>{line}</div>
											))}
										</div>
									</div>
								))}
							</div>
						)}

						{selectedDayEvents.length === 0 && !isLoading && (
							<div
								css={css`
									padding: 1rem 0;
									color: ${textMuted};
								`}
							>
								{translate("No availability events for this day.")}
							</div>
						)}
					</div>
				)}
			</div>
		</section>
	);
};
